import { useState, useEffect, useRef, useMemo } from 'react'
import { useWallet } from '@solana/wallet-adapter-react'
import BN from 'bn.js'
import {
  X,
  ArrowDownLeft,
  Info,
  CheckCircle,
  AlertTriangle,
  Clock,
  Shield,
  Percent,
  DollarSign,
  Calculator
} from 'lucide-react'
import { cn } from '../../lib/utils'

/**
 * Props for the WithdrawModal component
 */
interface WithdrawModalProps {
  isOpen: boolean
  onClose: () => void
  onWithdraw: (amount?: BN) => Promise<void>
  maxAmount: number
  tokenSymbol?: string
  loading?: boolean
  error?: string | null
}

/**
 * Enhanced WithdrawModal Component - Premium glassmorphism withdrawal interface
 * 
 * This component provides:
 * - Professional glassmorphism modal design
 * - Flexible withdrawal by amount or percentage
 * - Real-time calculation of fees and final amount
 * - Input validation with visual feedback
 * - Responsive design optimized for all devices
 * - Accessibility-compliant modal structure
 * - Premium DeFi platform aesthetics
 */
export function WithdrawModal({
  isOpen,
  onClose,
  onWithdraw,
  maxAmount,
  tokenSymbol = 'SOL',
  loading = false,
  error = null
}: WithdrawModalProps): JSX.Element | null {
  const [withdrawalType, setWithdrawalType] = useState<'amount' | 'percentage'>('percentage')
  const [amount, setAmount] = useState('')
  const [percentage, setPercentage] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const { connected } = useWallet()

  /**
   * Calculate withdrawal details based on input
   */
  const calculations = useMemo(() => {
    let withdrawalAmount = 0
    let withdrawalPercentage = 0

    if (withdrawalType === 'amount') {
      withdrawalAmount = parseFloat(amount) || 0
      withdrawalPercentage = maxAmount > 0 ? (withdrawalAmount / maxAmount) * 100 : 0
    } else {
      withdrawalPercentage = parseFloat(percentage) || 0
      withdrawalAmount = (withdrawalPercentage / 100) * maxAmount
    }

    // Mock fee for display - actual fee handled by contract
    const withdrawalFeeAmount = 0
    const netAmount = withdrawalAmount - withdrawalFeeAmount
    const remainingValue = maxAmount - withdrawalAmount

    return {
      withdrawalAmount,
      withdrawalPercentage,
      withdrawalFeeAmount,
      netAmount,
      remainingValue
    }
  }, [withdrawalType, amount, percentage, maxAmount])

  /**
   * Validate withdrawal input
   */
  const validateInput = (value: string, type: 'amount' | 'percentage') => {
    const numValue = parseFloat(value)
    
    if (isNaN(numValue) || numValue <= 0) {
      setValidationError('Please enter a valid value')
      return false
    }

    if (type === 'amount') {
      if (numValue > maxAmount) {
        setValidationError(`Maximum withdrawal is ${maxAmount.toFixed(4)} ${tokenSymbol}`)
        return false
      }
    } else {
      if (numValue > 100) {
        setValidationError('Percentage cannot exceed 100%')
        return false
      }
    }
    
    setValidationError(null)
    return true
  }

  /**
   * Handle input changes
   */
  const handleInputChange = (value: string, type: 'amount' | 'percentage') => {
    // Allow only numbers and decimal point
    const sanitizedValue = value.replace(/[^0-9.]/g, '')
    
    // Prevent multiple decimal points
    const decimalCount = (sanitizedValue.match(/\./g) || []).length
    if (decimalCount > 1) return
    
    if (type === 'amount') {
      setAmount(sanitizedValue)
      if (sanitizedValue) {
        validateInput(sanitizedValue, 'amount')
      } else {
        setValidationError(null)
      }
    } else {
      setPercentage(sanitizedValue)
      if (sanitizedValue) {
        validateInput(sanitizedValue, 'percentage')
      } else {
        setValidationError(null)
      }
    }
  }

  /**
   * Handle withdrawal confirmation
   */
  const handleConfirm = async () => {
    const currentValue = withdrawalType === 'amount' ? amount : percentage
    if (!validateInput(currentValue, withdrawalType) || confirming) return

    setConfirming(true)
    try {
      let withdrawAmountBN: BN | undefined
      
      // If withdrawing 100% or very close to max, pass undefined to withdraw all
      if (calculations.withdrawalPercentage >= 99.99) {
        withdrawAmountBN = undefined
      } else {
        withdrawAmountBN = new BN(calculations.withdrawalAmount * 1e9)
      }

      await onWithdraw(withdrawAmountBN)
      onClose()
      setAmount('')
      setPercentage('')
      setValidationError(null)
    } catch (error) {
      console.error('Withdrawal failed:', error)
    } finally {
      setConfirming(false)
    }
  }

  /**
   * Handle modal close
   */
  const handleClose = () => {
    if (confirming) return
    onClose()
    setAmount('')
    setPercentage('')
    setValidationError(null)
  }

  /**
   * Handle keyboard shortcuts
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const currentValue = withdrawalType === 'amount' ? amount : percentage
    if (e.key === 'Enter' && !validationError && currentValue && !confirming) {
      handleConfirm()
    }
    if (e.key === 'Escape') {
      handleClose()
    }
  }

  /**
   * Set preset percentages
   */
  const setPresetPercentage = (value: number) => {
    const percentageStr = value.toString()
    setPercentage(percentageStr)
    validateInput(percentageStr, 'percentage')
  }

  // Auto-focus input when modal opens or type changes
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus()
      }, 100)
    }
  }, [isOpen, withdrawalType])

  if (!isOpen) return null

  const currentValue = withdrawalType === 'amount' ? amount : percentage
  const isValid = !validationError && currentValue && parseFloat(currentValue) > 0
  const presetPercentages = [25, 50, 75, 100]

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-50 bg-dark-bg/60 backdrop-blur-sm animate-backdrop-in"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div 
          className="modal-content w-full max-w-md animate-modal-in"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={handleKeyDown}
          role="dialog"
          aria-labelledby="withdraw-modal-title"
          aria-describedby="withdraw-modal-description"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-dark-border/30">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-cyber-gradient rounded-cyber flex items-center justify-center">
                <ArrowDownLeft className="w-5 h-5 text-dark-bg" />
              </div>
              <div>
                <h3 id="withdraw-modal-title" className="text-xl font-header text-gradient-cyber">
                  Withdraw Funds
                </h3>
                <p id="withdraw-modal-description" className="text-sm text-text-muted">
                  Withdraw your pool shares as SOL
                </p>
              </div>
            </div>
            
            <button
              onClick={handleClose}
              disabled={confirming}
              className="p-2 rounded-cyber hover:bg-dark-panel/50 transition-colors duration-200 disabled:opacity-50"
              aria-label="Close modal"
            >
              <X className="w-5 h-5 text-text-muted" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Error Display */}
            {error && (
              <div className="glass-card bg-status-error/10 border-status-error/20 p-4">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-5 h-5 text-status-error flex-shrink-0" />
                  <span className="text-status-error text-sm">{error}</span>
                </div>
              </div>
            )}

            {/* Your Holdings */}
            <div className="glass-card p-4">
              <h4 className="text-sm font-medium text-cyber-cyan mb-3 flex items-center space-x-2">
                <Shield className="w-4 h-4" />
                <span>Your Holdings</span>
              </h4>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-text-muted">Available to Withdraw</div>
                  <div className="font-mono text-text-primary">
                    {maxAmount.toFixed(4)} {tokenSymbol}
                  </div>
                </div>
              </div>
            </div>

            {/* Withdrawal Type Selector */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-3">
                Withdrawal Method
              </label>
              <div className="flex items-center space-x-1 glass-dark rounded-cyber p-1">
                <button
                  onClick={() => setWithdrawalType('percentage')}
                  className={cn(
                    "flex-1 flex items-center justify-center space-x-2 px-3 py-2 text-sm font-medium rounded-cyber transition-all duration-300",
                    withdrawalType === 'percentage'
                      ? "bg-cyber-magenta text-dark-bg shadow-neon-magenta"
                      : "text-text-secondary hover:text-cyber-magenta hover:bg-dark-panel/30"
                  )}
                >
                  <Percent className="w-4 h-4" />
                  <span>Percentage</span>
                </button>
                <button
                  onClick={() => setWithdrawalType('amount')}
                  className={cn(
                    "flex-1 flex items-center justify-center space-x-2 px-3 py-2 text-sm font-medium rounded-cyber transition-all duration-300",
                    withdrawalType === 'amount'
                      ? "bg-cyber-cyan text-dark-bg shadow-neon-cyan"
                      : "text-text-secondary hover:text-cyber-cyan hover:bg-dark-panel/30"
                  )}
                >
                  <DollarSign className="w-4 h-4" />
                  <span>Amount</span>
                </button>
              </div>
            </div>

            {/* Input Section */}
            <div className="space-y-4">
              {withdrawalType === 'percentage' ? (
                <>
                  <div>
                    <label htmlFor="withdrawal-percentage" className="block text-sm font-medium text-text-secondary mb-2">
                      Withdrawal Percentage
                    </label>
                    <div className="relative">
                      <input
                        id="withdrawal-percentage"
                        ref={inputRef}
                        type="text"
                        value={percentage}
                        onChange={(e) => handleInputChange(e.target.value, 'percentage')}
                        placeholder="0"
                        className={cn(
                          "cyber-input w-full pl-12 pr-16 py-4 text-2xl font-mono text-center",
                          validationError && "border-status-error focus:border-status-error",
                          isValid && "border-status-success focus:border-status-success"
                        )}
                        disabled={confirming}
                      />
                      <Percent className="absolute left-4 top-1/2 transform -translate-y-1/2 w-6 h-6 text-cyber-magenta" />
                      <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-cyber-magenta font-medium">
                        %
                      </span>
                    </div>
                  </div>

                  {/* Preset Percentages */}
                  <div>
                    <div className="text-sm text-text-muted mb-2">Quick Select</div>
                    <div className="grid grid-cols-4 gap-2">
                      {presetPercentages.map((preset) => (
                        <button
                          key={preset}
                          onClick={() => setPresetPercentage(preset)}
                          disabled={confirming}
                          className={cn(
                            "p-2 text-sm font-medium rounded-cyber transition-all duration-200",
                            percentage === preset.toString()
                              ? "bg-cyber-magenta text-dark-bg"
                              : "glass-dark hover:bg-dark-panel/70 text-text-secondary hover:text-cyber-magenta"
                          )}
                        >
                          {preset}%
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div>
                  <label htmlFor="withdrawal-amount" className="block text-sm font-medium text-text-secondary mb-2">
                    Withdrawal Amount (SOL)
                  </label>
                  <div className="relative">
                    <input
                      id="withdrawal-amount"
                      ref={inputRef}
                      type="text"
                      value={amount}
                      onChange={(e) => handleInputChange(e.target.value, 'amount')}
                      placeholder="0.00"
                      className={cn(
                        "cyber-input w-full pl-12 pr-16 py-4 text-2xl font-mono text-center",
                        validationError && "border-status-error focus:border-status-error",
                        isValid && "border-status-success focus:border-status-success"
                      )}
                      disabled={confirming}
                    />
                    <DollarSign className="absolute left-4 top-1/2 transform -translate-y-1/2 w-6 h-6 text-cyber-cyan" />
                    <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-cyber-cyan font-medium">
                      {tokenSymbol}
                    </span>
                  </div>
                </div>
              )}

              {/* Validation Messages */}
              {validationError && (
                <div className="flex items-center space-x-2 text-status-error text-sm">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  <span>{validationError}</span>
                </div>
              )}

              {isValid && !validationError && (
                <div className="flex items-center space-x-2 text-status-success text-sm">
                  <CheckCircle className="w-4 h-4 flex-shrink-0" />
                  <span>Valid withdrawal {withdrawalType}</span>
                </div>
              )}
            </div>

            {/* Calculation Preview */}
            {currentValue && !validationError && (
              <div className="glass-card p-4 space-y-4">
                <h4 className="text-sm font-medium text-cyber-cyan flex items-center space-x-2">
                  <Calculator className="w-4 h-4" />
                  <span>Withdrawal Summary</span>
                </h4>

                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-text-secondary">Gross Amount</span>
                    <span className="font-mono text-lg text-text-primary">
                      {calculations.withdrawalAmount.toFixed(4)} {tokenSymbol}
                    </span>
                  </div>

                  <div className="flex justify-between items-center pt-2 border-t border-dark-border/30">
                    <span className="text-text-secondary font-medium">Net Amount</span>
                    <span className="font-mono text-lg text-cyber-green">
                      {calculations.netAmount.toFixed(4)} {tokenSymbol}
                    </span>
                  </div>
                </div>

                {/* Remaining Holdings */}
                <div className="glass-card bg-dark-surface/50 p-3">
                  <div className="text-xs text-text-muted mb-2 font-medium">REMAINING HOLDINGS</div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-text-secondary">Value</span>
                    <span className="font-mono text-text-primary">
                      {calculations.remainingValue.toFixed(4)} {tokenSymbol}
                    </span>
                  </div>
                </div>

                <div className="glass-card bg-cyber-cyan/5 p-3">
                  <div className="flex items-start space-x-2">
                    <Info className="w-4 h-4 text-cyber-cyan flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-text-muted">
                      Withdrawal fees help maintain pool stability and cover operational costs.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Connection Check */}
            {!connected && (
              <div className="glass-card bg-status-warning/10 border-status-warning/20 p-4">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-5 h-5 text-status-warning flex-shrink-0" />
                  <span className="text-status-warning text-sm">
                    Connect your wallet to withdraw funds
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between space-x-3 p-6 border-t border-dark-border/30">
            <button
              onClick={handleClose}
              disabled={confirming}
              className="cyber-button flex-1 disabled:opacity-50"
            >
              Cancel
            </button>
            
            <button
              onClick={handleConfirm}
              disabled={!isValid || !connected || confirming || loading}
              className="cyber-button-secondary flex-1 flex items-center justify-center space-x-2 disabled:opacity-50 border-status-error text-status-error hover:bg-status-error hover:text-white"
            >
              {confirming || loading ? (
                <>
                  <Clock className="w-4 h-4 animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <ArrowDownLeft className="w-4 h-4" />
                  <span>
                    Withdraw {withdrawalType === 'percentage' 
                      ? `${percentage || '0'}%` 
                      : `${amount || '0'} SOL`}
                  </span>
                </>
              )}
            </button>
          </div>

          {/* Loading Overlay */}
          {(confirming || loading) && (
            <div className="absolute inset-0 bg-dark-bg/50 backdrop-blur-sm rounded-cyber flex items-center justify-center">
              <div className="glass-card p-6 text-center">
                <div className="w-12 h-12 border-2 border-cyber-magenta border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <div className="text-cyber-magenta font-medium">
                  {confirming ? 'Confirming withdrawal...' : 'Processing...'}
                </div>
                <div className="text-sm text-text-muted mt-1">
                  Please confirm in your wallet
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default WithdrawModal